import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Link, Box, IconButton, Menu, MenuItem, Tooltip, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControlLabel, Switch, Collapse
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';

function parseTagsInput(s) {
    // English comment: Accept comma-separated tags and normalize.
    return (s || "")
        .split(",")
        .map(t => t.trim())
        .filter(Boolean);
}

function extractDomain(input) {
    // English comment: Try to extract hostname from an URL. Fallback to input trimmed.
    const raw = (input || "").trim();
    if (!raw) return "";

    try {
        const withProto = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw)
            ? raw
            : `https://${raw}`;

        const u = new URL(withProto);

        return (u.hostname || "").replace(/^www\./i, "");
    } catch {
        return raw.replace(/^www\./i, "");
    }
}

function SearchResults({
    results = [],
    handleExport,
    loading = false,
    hasMore = false,
    handleLoadMore,

    onMarkLinkList,
    onBlockDomain,

    markError = "",
    markSuccess = "",
    clearMarkMessages,
    marking = false,
}) {

    const [hasStarted, setHasStarted] = useState(false);

    // column toggles
    const [showStreet, setShowStreet] = useState(false);
    const [showZip, setShowZip] = useState(false);
    const [showCountry, setShowCountry] = useState(false);
    const [showFirstname, setShowFirstname] = useState(false);
    const [showSnippet, setShowSnippet] = useState(true);

    // phone filter toggle
    const [showWithoutPhone, setShowWithoutPhone] = useState(false);

    // filter panel
    const [showFilters, setShowFilters] = useState(false);

    // per-row menu state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    // LinkList dialog state
    const [tagOpen, setTagOpen] = useState(false);
    const [tagUrl, setTagUrl] = useState("");
    const [tagInput, setTagInput] = useState("");

    // Blocked domain dialog state
    const [blockOpen, setBlockOpen] = useState(false);
    const [blockUrl, setBlockUrl] = useState("");
    const [blockDomain, setBlockDomain] = useState("");
    const [blockTags, setBlockTags] = useState("");
    const [blockIsActive, setBlockIsActive] = useState(true);

    const hasResults = results && results.length > 0;

    useEffect(() => {

        if (loading) {
            setHasStarted(true);
        }

    }, [loading]);

    if (!hasStarted && !loading && !hasResults) {
        return null;
    }

    // English comment: Filter rows on frontend only.
    const filteredResults = showWithoutPhone
        ? (results || [])
        : (results || []).filter((r) => {

            const p = (r?.phonenumber ?? "").toString().trim();

            if (!p) return false;
            if (p === "-") return false;
            if (p === "—") return false;

            return true;
        });

    // Base columns:
    // Firma, Ort, Nachname, Telefonnummer, Email, URL, Actions = 7
    const columnCount =
        7 +
        (showStreet ? 1 : 0) +
        (showZip ? 1 : 0) +
        (showCountry ? 1 : 0) +
        (showFirstname ? 1 : 0) +
        (showSnippet ? 1 : 0);

    const TypingRow = () => (
        <TableRow>
            {Array.from({ length: columnCount }).map((_, i) => (
                <TableCell key={i} sx={{ borderBottom: 'none' }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '18px',
                            borderRadius: '4px',
                            bgcolor: 'rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                            position: 'relative',

                            '@keyframes shimmer': {
                                '0%': { left: '-100%' },
                                '100%': { left: '100%' },
                            },

                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '50%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                animation: 'shimmer 1.8s infinite',
                            },
                        }}
                    />
                </TableCell>
            ))}
        </TableRow>
    );

    const openMenu = (e, row) => {
        setMenuAnchor(e.currentTarget);
        setMenuRow(row);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuRow(null);
    };

    // LinkList flow
    const openTagDialog = () => {

        const url = (menuRow?.link || "").trim();

        closeMenu();

        if (!url) return;

        setTagUrl(url);
        setTagInput("");
        setTagOpen(true);
    };

    const closeTagDialog = () => {
        setTagOpen(false);
        setTagUrl("");
        setTagInput("");
    };

    const handleConfirmMark = async () => {

        const url = (tagUrl || "").trim();
        const tags = parseTagsInput(tagInput);

        closeTagDialog();

        if (onMarkLinkList) {
            await onMarkLinkList(url, tags);
        }
    };

    // Block domain flow
    const openBlockDialog = () => {

        const url = (menuRow?.link || "").trim();

        closeMenu();

        if (!url) return;

        const domain = extractDomain(url);

        setBlockUrl(url);
        setBlockDomain(domain);
        setBlockTags("");
        setBlockIsActive(true);
        setBlockOpen(true);
    };

    const closeBlockDialog = () => {
        setBlockOpen(false);
        setBlockUrl("");
        setBlockDomain("");
        setBlockTags("");
        setBlockIsActive(true);
    };

    const handleConfirmBlock = async () => {

        const domain = (blockDomain || "").trim();
        const tags = parseTagsInput(blockTags);

        closeBlockDialog();

        if (onBlockDomain) {
            await onBlockDomain(domain, tags, blockIsActive);
        }
    };

    return (
        <TableContainer
            component={Paper}
            sx={{
                mt: 5,
                width: '98%',
                mx: 'auto',
                overflowX: 'auto',
                pb: 2
            }}
        >

            {hasResults && (
                <Box sx={{ ml: 2, mt: 2, mb: 2, mr: 2 }}>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap"
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleExport}
                        >
                            Export to Excel
                        </Button>

                        <Button
                            variant={showFilters ? "contained" : "outlined"}
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(v => !v)}
                        >
                            Filter
                        </Button>
                    </Box>

                    <Collapse
                        in={showFilters}
                        timeout={200}
                        unmountOnExit
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                                mt: 2
                            }}
                        >

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showStreet}
                                        onChange={(e) => setShowStreet(e.target.checked)}
                                    />
                                }
                                label="Straße anzeigen"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showZip}
                                        onChange={(e) => setShowZip(e.target.checked)}
                                    />
                                }
                                label="PLZ anzeigen"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showCountry}
                                        onChange={(e) => setShowCountry(e.target.checked)}
                                    />
                                }
                                label="Land anzeigen"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showFirstname}
                                        onChange={(e) => setShowFirstname(e.target.checked)}
                                    />
                                }
                                label="Vorname anzeigen"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showSnippet}
                                        onChange={(e) => setShowSnippet(e.target.checked)}
                                    />
                                }
                                label="Snippet anzeigen"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showWithoutPhone}
                                        onChange={(e) => setShowWithoutPhone(e.target.checked)}
                                    />
                                }
                                label="Ergebnisse ohne Telefonnummer anzeigen"
                            />

                        </Box>
                    </Collapse>

                </Box>
            )}

            <Table size="small">

                <TableHead>
                    <TableRow>

                        <TableCell>Firma</TableCell>

                        {showStreet && (
                            <TableCell>
                                Strasse
                            </TableCell>
                        )}

                        {showZip && (
                            <TableCell sx={{ width: 80 }}>
                                Plz
                            </TableCell>
                        )}

                        <TableCell>Ort</TableCell>

                        {showCountry && (
                            <TableCell sx={{ width: 80 }}>
                                Land
                            </TableCell>
                        )}

                        {showFirstname && (
                            <TableCell
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                }}
                            >
                                Vorname
                            </TableCell>
                        )}

                        <TableCell
                            sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word'
                            }}
                        >
                            Nachname
                        </TableCell>

                        <TableCell>Telefonnummer</TableCell>
                        <TableCell>Email</TableCell>

                        <TableCell sx={{ maxWidth: 200 }}>
                            URL
                        </TableCell>

                        {showSnippet && (
                            <TableCell sx={{ maxWidth: 300 }}>
                                Snippet
                            </TableCell>
                        )}

                        <TableCell
                            align="right"
                            sx={{ width: 60 }}
                        />

                    </TableRow>
                </TableHead>

                <TableBody>

                    {filteredResults.map((result, index) => (
                        <TableRow key={index}>

                            <TableCell sx={{ wordBreak: 'break-word' }}>
                                {result.title || '—'}
                            </TableCell>

                            {showStreet && (
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {result.street || '—'}
                                </TableCell>
                            )}

                            {showZip && (
                                <TableCell>
                                    {result.zipcode || '—'}
                                </TableCell>
                            )}

                            <TableCell sx={{ wordBreak: 'break-word' }}>
                                {result.city || '—'}
                            </TableCell>

                            {showCountry && (
                                <TableCell>
                                    {result.country || '—'}
                                </TableCell>
                            )}

                            {showFirstname && (
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {result.firstname || '—'}
                                </TableCell>
                            )}

                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {result.lastname || '—'}
                            </TableCell>

                            <TableCell sx={{ wordBreak: 'break-word' }}>
                                {result.phonenumber || '—'}
                            </TableCell>

                            <TableCell sx={{ wordBreak: 'break-word' }}>
                                {result.email || '—'}
                            </TableCell>

                            <TableCell sx={{ wordBreak: 'break-all' }}>
                                {result.link ? (
                                    <Link
                                        href={result.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                    >
                                        {result.link}
                                    </Link>
                                ) : '—'}
                            </TableCell>

                            {showSnippet && (
                                <TableCell
                                    sx={{
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {result.snippet || '—'}
                                </TableCell>
                            )}

                            <TableCell
                                align="right"
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                <Tooltip title="Aktionen">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openMenu(e, result)}
                                            disabled={marking}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>

                        </TableRow>
                    ))}

                    {loading && <TypingRow />}

                </TableBody>

            </Table>

            {hasMore && !loading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                    >
                        Mehr top Leads laden
                    </Button>
                </Box>
            )}

            {/* Row action menu */}
            <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={closeMenu}
            >
                <MenuItem
                    onClick={openTagDialog}
                    disabled={!menuRow?.link || marking}
                >
                    Als Link Verzeichnis markieren
                </MenuItem>

                <MenuItem
                    onClick={openBlockDialog}
                    disabled={!menuRow?.link || marking}
                >
                    Domain excluden
                </MenuItem>
            </Menu>

            {/* LinkList Tag Dialog */}
            <Dialog
                open={tagOpen}
                onClose={closeTagDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Als Link Verzeichnis markieren
                </DialogTitle>

                <DialogContent dividers>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            mt: 1
                        }}
                    >
                        <TextField
                            label="URL"
                            value={tagUrl}
                            InputProps={{ readOnly: true }}
                        />

                        <TextField
                            label="Tags (kommagetrennt)"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            placeholder="seo, partner, verzeichnis"
                            helperText="Optional. Mehrere Tags mit Komma trennen."
                            disabled={marking}
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={closeTagDialog}
                        disabled={marking}
                    >
                        Abbrechen
                    </Button>

                    <Button
                        onClick={handleConfirmMark}
                        variant="contained"
                        disabled={marking}
                    >
                        {marking ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Blocked Domain Dialog */}
            <Dialog
                open={blockOpen}
                onClose={closeBlockDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Domain excluden
                </DialogTitle>

                <DialogContent dividers>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            mt: 1
                        }}
                    >
                        <TextField
                            label="Quelle (URL)"
                            value={blockUrl}
                            InputProps={{ readOnly: true }}
                        />

                        <TextField
                            label="Domain*"
                            value={blockDomain}
                            onChange={(e) => setBlockDomain(e.target.value)}
                            placeholder="example.com"
                            helperText="Wird aus der URL abgeleitet, kann aber angepasst werden."
                            disabled={marking}
                        />

                        <TextField
                            label="Tags (kommagetrennt)"
                            value={blockTags}
                            onChange={(e) => setBlockTags(e.target.value)}
                            placeholder="spam, irrelevant"
                            helperText="Optional. Mehrere Tags mit Komma trennen."
                            disabled={marking}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={blockIsActive}
                                    onChange={(e) => setBlockIsActive(e.target.checked)}
                                    disabled={marking}
                                />
                            }
                            label="Blockierung aktiv"
                        />
                    </Box>
                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={closeBlockDialog}
                        disabled={marking}
                    >
                        Abbrechen
                    </Button>

                    <Button
                        onClick={handleConfirmBlock}
                        variant="contained"
                        color="error"
                        disabled={marking || !blockDomain.trim()}
                    >
                        {marking ? "Speichern…" : "Excluden"}
                    </Button>

                </DialogActions>
            </Dialog>

            {/* Feedback */}
            <Snackbar
                open={!!markError || !!markSuccess}
                autoHideDuration={4000}
                onClose={clearMarkMessages}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                }}
            >
                {markError ? (
                    <Alert
                        severity="error"
                        onClose={clearMarkMessages}
                        sx={{ width: "100%" }}
                    >
                        {markError}
                    </Alert>
                ) : (
                    <Alert
                        severity="success"
                        onClose={clearMarkMessages}
                        sx={{ width: "100%" }}
                    >
                        {markSuccess}
                    </Alert>
                )}
            </Snackbar>

        </TableContainer>
    );
}

export default SearchResults;